


/**
 * Team Review Service
 * Handles all API calls related to team reviews
 */

import { apiService } from './api';

// Feedback interfaces
export interface TeamReviewFeedback {
  employeeFeedbackRating: string;
  appreciativeFeedback: string[];
  constructiveFeedback: string[];
}

export interface TeamReviewFeedbackResponse {
  status: boolean;
  data: TeamReviewFeedback | null;
  message?: string;
  error?: string;
}

// OKRs interfaces
export interface TeamReviewOKR {
  sno: number;
  objective: string;
  target: string;
  endYearResults: string;
  rating: number;
}

export interface TeamReviewOKRsData {
  okrs: TeamReviewOKR[];
  hod_rating?: number; // Optional field for HOD rating
}

export interface TeamReviewOKRsResponse {
  status: boolean;
  data: TeamReviewOKRsData | null;
  message?: string;
  error?: string;
}

interface TeamReviewData {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  reviewStatus: 'Pending' | 'Submitted';
  employeeEmail: string;
}

interface TeamReviewListResponse {
  status: boolean;
  data: {
    team_review_list: any[];
  };
  message?: string;
  error?: string;
}

class HODReviewService {
  /**
   * Get team review list for a manager
   */
  async getTeamReviewList(managerEmail: string): Promise<TeamReviewListResponse> {
    try {
      console.log('🔄 hodReviewIconReviewService - Fetching team review list for:', managerEmail);
      
      const response = await apiService.post('/team-review/list', {
        manager_email: managerEmail
      });
      
      console.log('📋 hodReviewIconReviewService - API Response:', response);
      
      return {
        status: true,
        data: (response.data as any) || { team_review_list: [] },
        message: 'Team reviews fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching team reviews:', error);
      
      return {
        status: false,
        data: { team_review_list: [] },
        error: error.response?.data?.message || error.message || 'Failed to fetch team reviews'
      };
    }
  }

  /**
   * Transform API data to component format
   */
  transformApiData(apiData: any[]): TeamReviewData[] {
    if (!Array.isArray(apiData)) {
      console.warn('⚠️ hodReviewIconReviewService - Invalid API data format, expected array');
      return [];
    }

    return apiData.map((item: any) => ({
      id: item.id || item.review_id || String(Math.random()),
      employeeId: item.employee_id || item.employeeId || '',
      employeeName: item.employee_name || item.employeeName || '',
      designation: item.designation || '',
      department: item.department || '',
      managerName: item.manager_name || item.managerName || '',
      reviewStatus: item.review_status || item.reviewStatus || 'Pending',
      employeeEmail: item.employee_email || item.employeeEmail || ''
    }));
  }

  /**
   * Get team review nomination list
   */
  async getHodNominationReviewList(email: string): Promise<any> {
    try {
      console.log('🔄 hodReviewIconReviewService - Fetching team nomination review list for:', email);
      
      const response = await apiService.get(`/api/v1/feedback/hod_nomination_review_list/?email=${email}`);
      
      console.log('📋 hodReviewIconReviewService - Team nomination review list response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Team nomination review list fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching team nomination review list:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch team nomination review list'
      };
    }
  }

  /**
   * Get team review details by ID
   */
  async getTeamReviewDetails(reviewId: string, managerEmail: string): Promise<any> {
    try {
      console.log('🔄 hodReviewIconReviewService - Fetching review details for:', reviewId);
      
      const response = await apiService.post('/team-review/details', {
        review_id: reviewId,
        manager_email: managerEmail
      });
      
      console.log('📋 hodReviewIconReviewService - Review details response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Review details fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching review details:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch review details'
      };
    }
  }
  /**
   * Get team review feedback by employee ID
   */
  async getTeamReviewFeedback(employeeId: string, managerEmail: string): Promise<TeamReviewFeedbackResponse> {
    try {
      console.log('🔄 hodReviewIconReviewService - Fetching feedback for employee:', employeeId);
      
      const response = await apiService.post('/team-review/feedback', {
        employee_id: employeeId,
        manager_email: managerEmail
      });
      
      console.log('📋 hodReviewIconReviewService - Feedback response:', response);
      
      return {
        status: true,
        data: response.data as unknown as TeamReviewFeedback,
        message: 'Feedback fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching feedback:', error);
      
      // Return dummy data for now
      const dummyData: TeamReviewFeedback = {
        employeeFeedbackRating: "3.4/5.0",
        appreciativeFeedback: [
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
        ],
        constructiveFeedback: [
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
        ]
      };
      
      return {
        status: false,
        data: dummyData,
        error: error.response?.data?.message || error.message || 'Failed to fetch feedback, using dummy data'
      };
    }
  }

  /**
   * Get team review OKRs by employee ID (legacy method)
   */
  async getTeamReviewOKRs(employeeId: string, managerEmail: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('🔄 hodReviewIconReviewService - Fetching OKRs for employee:', employeeId);
      
      const response = await apiService.post('/team-review/okrs', {
        employee_id: employeeId,
        manager_email: managerEmail
      });
      
      console.log('📋 hodReviewIconReviewService - OKRs response:', response);
      
      return {
        status: true,
        data: response.data as unknown as TeamReviewOKRsData,
        message: 'OKRs fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching OKRs:', error);
      
      // Return dummy data for now
      const dummyData: TeamReviewOKRsData = {
        okrs: [
          {
            sno: 1,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorem",
            rating: 5
          },
          {
            sno: 2,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Ipsum",
            rating: 5
          },
          {
            sno: 3,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorsum",
            rating: 5
          },
          {
            sno: 4,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorsum",
            rating: 4
          },
          {
            sno: 5,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorsum",
            rating: 3
          }
        ]
      };
      
      return {
        status: false,
        data: dummyData,
        error: error.response?.data?.message || error.message || 'Failed to fetch OKRs, using dummy data'
      };
    }
  }

  /**
   * Get self appraisal OKRs by email and employee ID
   */
  async getSelfAppraisalOKRs(email: string, empId: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('===== SERVICE: STARTING OKRS API CALL =====');
      console.log('SERVICE Step 1 - Input parameters:');
      console.log('  - Email:', email);
      console.log('  - Employee ID:', empId);
      
      const apiUrl = `/api/v1/feedback/admin/self_appraisal/okr/?email=${encodeURIComponent(email)}&emp_id=${encodeURIComponent(empId)}`;
      console.log('  - Full API URL:', apiUrl);
      
      console.log('SERVICE Step 2 - Making API call...');
      const response = await apiService.get(apiUrl);

   
      
      console.log('SERVICE Step 3 - Got API response:');
      console.log('  - Response exists:', !!response);
      console.log('  - Response.data exists:', !!response.data);
      console.log('  - Response type:', typeof response);
      console.log('  - Response.data type:', typeof response.data);
      console.log('SERVICE - Full response object:', response);
      console.log('SERVICE - Raw response.data:', JSON.stringify(response.data, null, 2));
      
      // Transform the response data to match our interface
      let transformedOKRs: TeamReviewOKR[] = [];
      
   
      // Extract OKRs directly from the correct API response path

      const apiResponseData = (response.data as any);
      
      // Use the correct extraction path that works in the alert
      const okrs = apiResponseData?.self_appraisal_list?.[0]?.okrs || [];
      
      if (Array.isArray(okrs) && okrs.length > 0) {
        console.log('🔍 Debug - Raw okrs array:', okrs);
        transformedOKRs = okrs.map((okr: any, index: number) => ({
          sno: index + 1,
          objective: okr.objective || '',
          target: okr.target || '',
          endYearResults: okr.eyr || '',
          rating: okr.rating || 0
        }));
        console.log('🔍 Debug - transformedOKRs:', transformedOKRs);
        console.log('🔍 Debug - transformedOKRs.length:', transformedOKRs.length);
      } else {

        if (apiResponseData?.self_appraisal_list?.[0]) {
          console.log('🔍 Debug - First appraisal item:', apiResponseData.self_appraisal_list[0]);
        }
      }
      
      const transformedData: TeamReviewOKRsData = {
        okrs: transformedOKRs
      };
      
      console.log('🔍 Debug - Final transformed data:', JSON.stringify(transformedData, null, 2));
      
      // Check if the API response indicates success
      const apiSuccess = (response.data as any)?.status === true;
      console.log('🔍 Debug - API success status:', apiSuccess);
      console.log('🔍 Debug - API response status value:', (response.data as any)?.status);
      
      return {
        status: true, // Always return true for now to see the data structure
        data: transformedData,
        message: apiSuccess ? 'Self appraisal OKRs fetched successfully' : 'No OKRs data available'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching self appraisal OKRs:', error);
      
      // Return dummy data for now
      const dummyData: TeamReviewOKRsData = {
        okrs: [
          {
            sno: 1,
            objective: "Improve customer satisfaction scores by 15%",
            target: "90% satisfaction",
            endYearResults: "92% achieved",
            rating: 5
          },
          {
            sno: 2,
            objective: "Lead digital transformation initiative",
            target: "Complete 3 projects",
            endYearResults: "4 projects completed",
            rating: 5
          },
          {
            sno: 3,
            objective: "Develop team capabilities through training",
            target: "Train 10 team members",
            endYearResults: "12 members trained",
            rating: 4
          },
          {
            sno: 4,
            objective: "Reduce operational costs by 10%",
            target: "$100K savings",
            endYearResults: "$85K saved",
            rating: 3
          },
          {
            sno: 5,
            objective: "Implement new quality processes",
            target: "2 new processes",
            endYearResults: "1 process implemented",
            rating: 2
          }
        ]
      };
      
      return {
        status: false,
        data: dummyData,
        error: error.response?.data?.message || error.message || 'Failed to fetch self appraisal OKRs, using dummy data'
      };
    }
  }

  /**
   * Get nomination review details for a specific employee
   */
  async getNominationReviewDetails(email: string, nominationRelationshipId: string): Promise<any> {
    try {
      console.log('🔄 hodReviewIconReviewService - Fetching nomination review details for:', { email, nominationRelationshipId });
      
      const response = await apiService.get(`/api/v1/feedback/hod_nomination_review/?email=${email}&nomination_relationship_id=${nominationRelationshipId}`);
      
      console.log('📋 hodReviewIconReviewService - Nomination review details response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review details fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error fetching nomination review details:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch nomination review details'
      };
    }
  }

  /**
   * Save nomination review feedback (Save as Draft or Submit)
   */
  async saveNominationReviewFeedback(data: {
    nomination_relationship_id: number;
    email: string;
    competency: {
      solution_focused: number;
      adapt_to_win: number;
      courage_to_act: number;
      act_for_enterprise: number;
      own_it: number;
    };
    feedback: {
      constructive: string;
      appreciative: string;
    };
    is_email_required?: string;
  }): Promise<any> {
    try {
      console.log('🔄 hodReviewIconReviewService - Saving nomination review feedback:', data);
      
      const response = await apiService.put('/api/v1/feedback/nomination_review/', data);
      
      console.log('✅ hodReviewIconReviewService - Nomination review feedback saved successfully:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review feedback saved successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error saving nomination review feedback:', error);
      
      // Handle API error response properly
      const apiError = error.response?.data;
      
      return {
        status: false,
        data: apiError?.data || null,
        message: apiError?.message || error.message || 'Failed to save nomination review feedback',
        error: apiError?.error || error.message || 'Failed to save nomination review feedback',
        status_code: apiError?.status_code || error.response?.status
      };
    }
  }

  /**
   * Submit nomination review feedback (Final submission)
   */
  async submitNominationReviewFeedback(data: {
    nomination_relationship_id: number;
    email: string;
    competency: {
      solution_focused: number;
      adapt_to_win: number;
      courage_to_act: number;
      act_for_enterprise: number;
      own_it: number;
    };
    feedback: {
      constructive: string;
      appreciative: string;
    };
    is_email_required?: string;
  }): Promise<any> {
    try {
      console.log('🔄 hodReviewIconReviewService - Submitting nomination review feedback:', data);
      
      const response = await apiService.post('/api/v1/feedback/nomination_review/', data);
      
      console.log('✅ hodReviewIconReviewService - Nomination review feedback submitted successfully:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review feedback submitted successfully'
      };
    } catch (error: any) {
      console.error('❌ hodReviewIconReviewService - Error submitting nomination review feedback:', error);
      
      // Handle API error response properly
      const apiError = error.response?.data;
      
      return {
        status: false,
        data: apiError?.data || null,
        message: apiError?.message || error.message || 'Failed to submit nomination review feedback',
        error: apiError?.error || error.message || 'Failed to submit nomination review feedback',
        status_code: apiError?.status_code || error.response?.status
      };
    }
  }

  /**
   * Perform bulk HOD nomination review
   * 
   * @param hodEmail - HOD's email address
   * @param reviewList - Array of nominations with their details
   * @returns Promise with bulk action response
   */
  async performBulkHodReview(
    hodEmail: string,
    reviewList: Array<{ nomination_id: number; nominator_email: string }>
  ): Promise<any> {
    try {
      const url = `/api/v1/feedback/hod_bulk_nomination_review/`;
      const payload = {
        hod_email: hodEmail,
        review_list: reviewList
      };
      
      console.log('🔄 Performing bulk HOD nomination review:', {
        url,
        reviewCount: reviewList.length,
        payload
      });

      const response = await apiService.post(url, payload);
      
      console.log('✅ Bulk HOD nomination review completed:', {
        status: response.status,
        message: response.message,
        data: response.data
      });
      
      return response;
    } catch (error: any) {
      console.error('❌ Error performing bulk HOD nomination review:', {
        error,
        reviewCount: reviewList.length,
        hodEmail
      });
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }

  // Character validation utility methods
  getCharacterCount(text: string): number {
    return text ? text.length : 0;
  }

  isValidCharacterCount(text: string): { isValid: boolean; count: number; message?: string } {
    const count = this.getCharacterCount(text);
    const MIN_CHARS = 10;
    const MAX_CHARS = 10000;

    if (count < MIN_CHARS) {
      return {
        isValid: false,
        count,
        message: `Minimum ${MIN_CHARS} characters required`
      };
    }

    if (count > MAX_CHARS) {
      return {
        isValid: false,
        count,
        message: `Maximum ${MAX_CHARS} characters allowed`
      };
    }

    return {
      isValid: true,
      count
    };
  }

  /**
   * Get HOD view OKRs for a specific employee
   */
  async getHodViewOKRs(hodEmail: string, empId: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('🔄 hodReviewService - Fetching HOD view OKRs for:', hodEmail, 'employee:', empId);
      
      const apiUrl = `/api/v1/feedback/hod/self_appraisal/okr/`;
      
      const response = await apiService.get(apiUrl, {
        params: {
          hod_email: hodEmail,
          emp_id: empId
        }
      });

      console.log('✅ HOD view OKRs fetched successfully:', response.data);
      
      const responseData = response.data as any;
      
      // Enhanced debugging
      console.log('🔍 Service - Response status:', responseData?.status);
      console.log('🔍 Service - Response data:', responseData?.data);
      console.log('🔍 Service - Response error:', responseData?.error);
      console.log('🔍 Service - Full response structure:', responseData);
      
      // Handle direct response format (not wrapped in {status: true, data: {...}})
      if (responseData && (responseData.self_appraisal_list || responseData.hod_rating !== undefined)) {
        return {
          status: true,
          data: responseData, // Use the response data directly
          message: 'HOD view OKRs fetched successfully'
        };
      } else if (responseData?.status && responseData?.data) {
        // Handle wrapped response format
        return {
          status: true,
          data: responseData.data,
          message: 'HOD view OKRs fetched successfully'
        };
      } else {
        return {
          status: false,
          data: null,
          error: responseData?.error || responseData?.message || 'Failed to fetch HOD view OKR data'
        };
      }
    } catch (error: any) {
      console.error('❌ hodReviewService - Error fetching HOD view OKRs:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch HOD view OKR data'
      };
    }
  }

  /**
   * Submit HOD OKR rating (PUT API)
   */
  async submitHodOKRRating(hodEmail: string, empId: string, hodRating: number): Promise<{status: boolean; data?: any; error?: string; message?: string}> {
    try {
      console.log('🔄 hodReviewService - Submitting HOD OKR rating:', hodEmail, 'employee:', empId, 'rating:', hodRating);
      
      const apiUrl = `/api/v1/feedback/hod/self_appraisal/okr/`;
      
      const payload = {
        emp_id: empId,
        hod_rating: hodRating,
        hod_email: hodEmail
      };

      const response = await apiService.put(apiUrl, payload);

      console.log('✅ HOD OKR rating submitted successfully:', response.data);
      
      const responseData = response.data as any;
      
      // Enhanced debugging
      // Note: apiService.put returns the full API body as ApiResponse, so we check
      // the top-level response.status/response.error (not the nested response.data fields).
      console.log('🔍 Service - Submit response status:', response.status);
      console.log('🔍 Service - Submit response data:', responseData);
      console.log('🔍 Service - Submit response error:', response.error);
      
      if (response.status === true) {
        return {
          status: true,
          data: responseData,
          message: response.message || 'HOD OKR rating submitted successfully'
        };
      } else {
        const errMsg = response.message ||
          (typeof response.error === 'string' ? response.error : null) ||
          'Failed to submit HOD OKR rating';
        return {
          status: false,
          message: errMsg,
          error: errMsg
        };
      }
    } catch (error: any) {
      console.error('❌ hodReviewService - Error submitting HOD OKR rating:', error);
      // error here is an ApiError from api.ts (has .message, no .response property)
      const errMsg = error.response?.data?.message || error.message || 'Failed to submit HOD OKR rating';
      return {
        status: false,
        message: errMsg,
        error: errMsg
      };
    }
  }
}

export const hodReviewService = new HODReviewService();